import {
  createContext,
  useContext,
  useEffect,
  type MutableRefObject,
} from 'react';
import { getBezierPath, Position, type EdgeProps } from '@xyflow/react';
import { zipTieWireKey, type ZipTie } from '@erp/workflows';
import {
  routeEdge,
  segmentLabel,
  segmentsToPath,
  zipTieWireFromEdge,
  type EdgeGeometry,
  type FlowHandlePosition,
} from 'src/lib/workflows/zipTieGeometry';

/** What every routed edge on a canvas needs to draw itself through the ties. */
export type ZipTieRenderState = {
  ties: readonly ZipTie[];
  hoveredWireKeys: ReadonlySet<string>;
  previewWireKeys: ReadonlySet<string>;
  /** Each routed edge registers its geometry here so a stroke can hit-test it. */
  endpoints: MutableRefObject<Map<string, EdgeGeometry>>;
};

export const ZipTieContext = createContext<ZipTieRenderState | null>(null);

const NO_KEYS: ReadonlySet<string> = new Set();

/** Wire keys of the hovered tie, so its strands highlight together. */
export function useHoveredWireKeys(
  ties: readonly ZipTie[],
  hoveredTieId: string | null,
): ReadonlySet<string> {
  const hovered = ties.find((tie) => tie.id === hoveredTieId);
  return hovered ? new Set(hovered.wires.map(zipTieWireKey)) : NO_KEYS;
}

function flowHandle(
  position: Position | undefined,
  fallback: FlowHandlePosition,
): FlowHandlePosition {
  if (
    position === Position.Left ||
    position === Position.Right ||
    position === Position.Top ||
    position === Position.Bottom
  ) {
    return position;
  }
  return fallback;
}

function routedPath(
  segments: ReturnType<typeof routeEdge>,
): [path: string, labelX: number, labelY: number] {
  const middle = segments[Math.floor((segments.length - 1) / 2)];
  const label = middle ? segmentLabel(middle) : { x: 0, y: 0 };
  return [segmentsToPath(segments), label.x, label.y];
}

/**
 * Path for an edge on a canvas that may have zip ties. Without a
 * `ZipTieContext` it is React Flow's own bezier. With one, the edge is routed
 * through every tie holding its wire and registers its geometry for strokes.
 * `bundled` is true while its tie is hovered or a stroke is about to tie it.
 */
export function useZipTieRoutedEdge({
  id,
  source,
  target,
  sourceHandleId,
  targetHandleId,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  data,
}: EdgeProps): {
  path: string;
  labelX: number;
  labelY: number;
  bundled: boolean;
} {
  const zipTies = useContext(ZipTieContext);
  const wire = zipTieWireFromEdge({
    source,
    target,
    sourceHandle: sourceHandleId,
    targetHandle: targetHandleId,
    data,
  });
  const geometry: EdgeGeometry | null =
    zipTies && wire
      ? {
          id,
          wire,
          source: { x: sourceX, y: sourceY },
          target: { x: targetX, y: targetY },
          sourcePosition: flowHandle(sourcePosition, 'right'),
          targetPosition: flowHandle(targetPosition, 'left'),
        }
      : null;
  const endpoints = zipTies?.endpoints;
  if (endpoints && geometry) endpoints.current.set(id, geometry);
  useEffect(() => {
    const map = endpoints?.current;
    return () => {
      map?.delete(id);
    };
  }, [endpoints, id]);

  const [path, labelX, labelY] =
    zipTies && geometry
      ? routedPath(routeEdge(geometry, zipTies.ties))
      : getBezierPath({
          sourceX,
          sourceY,
          sourcePosition,
          targetX,
          targetY,
          targetPosition,
        });
  const wireKey = wire ? zipTieWireKey(wire) : '';
  const bundled =
    wireKey !== '' &&
    (zipTies?.hoveredWireKeys.has(wireKey) === true ||
      zipTies?.previewWireKeys.has(wireKey) === true);
  return { path, labelX, labelY, bundled };
}
